import { useMemo } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import type {
  PublicCategoryStatus,
  PublicServiceStatus,
} from "@/v2-status-client/status/status.types"
import StatusDot from "@/v2-status-client/status/dot/StatusDot"

export type StatusCategorySidebarProps = {
  // Top-level categories under the active platform. Rendered in
  // input order so the navigation order matches the body's
  // accordion order.
  categories: PublicCategoryStatus[]
  // Anchor id of the row that's currently the deep-link target
  // (the `#category-…` hash, without the prefix). Drives the
  // pressed visual state so the customer can tell which entry
  // they last followed.
  activeCategoryId: string
  // Click handler. Receives the category id and (when present)
  // the sub-category id so consumers can jump straight to a
  // sub-category-deep-link without re-deriving the relationship.
  onSelect: (categoryId: string, subCategoryId?: string) => void
}

// Vertical sidebar that replaces the previous secondary horizontal
// pill bar. Lives inside the per-platform detail view as the left
// column of a two-column grid; the existing accordion stack lives
// in the right column. Two-level navigation (parent + nested
// sub-categories) handles the layer category drill-down without
// swallowing the rest of the page in a giant flat list.
//
// The sidebar itself is layout-agnostic: positioning / stickiness
// is the parent's responsibility (`StatusPlatformDetail`) so this
// component can be reused inside a drawer or a mobile sheet without
// tugging position styles along.
//
// Hidden from the document outline when it would only carry one
// entry — a single-link sidebar adds clutter without aiding
// navigation.
function StatusCategorySidebar(props: Readonly<StatusCategorySidebarProps>) {
  const { categories, activeCategoryId, onSelect } = props
  if (categories.length < 2) return null
  return (
    <nav
      aria-label="Category navigation"
      className={cn(
        "flex flex-col gap-1 rounded-lg border border-border/60 bg-background/40 p-2",
        "text-sm",
      )}
    >
      <ul className="m-0 flex list-none flex-col gap-0.5">
        {categories.map((c) => (
          <CategoryEntry
            key={c.id}
            category={c}
            activeCategoryId={activeCategoryId}
            onSelect={onSelect}
          />
        ))}
      </ul>
    </nav>
  )
}

type CategoryEntryProps = {
  category: PublicCategoryStatus
  activeCategoryId: string
  onSelect: (categoryId: string, subCategoryId?: string) => void
}

// Single sidebar entry. When the category carries sub-categories
// they're rendered as an indented list below the parent — the
// parent button still jumps to the parent (= "see all of these").
// Sub-categories don't keep their own `aria-pressed` state because
// we don't currently push their hash into the URL; if that changes,
// add a `subCategoryId` arg to `useActiveCategoryId`.
function CategoryEntry(props: Readonly<CategoryEntryProps>) {
  const { category, activeCategoryId, onSelect } = props
  const subs = category.subCategories ?? []
  const isActive = activeCategoryId === category.id
  const isParentOfActive = useMemo(
    () => subs.some((sub) => sub.id === activeCategoryId),
    [subs, activeCategoryId],
  )
  return (
    <li className="flex flex-col">
      <SidebarButton
        label={category.name}
        count={category.totalCount}
        status={category.status}
        isActive={isActive || isParentOfActive}
        onClick={() => onSelect(category.id)}
      />
      {subs.length > 0 && (
        <ul className="m-0 mt-0.5 flex list-none flex-col gap-0.5 pl-3">
          {subs.map((sub) => (
            <li key={sub.id}>
              <SidebarButton
                label={sub.name}
                count={sub.totalCount}
                status={sub.status}
                isActive={activeCategoryId === sub.id}
                onClick={() => onSelect(category.id, sub.id)}
                size="sm"
              />
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

type SidebarButtonProps = {
  label: string
  count: number
  status: PublicServiceStatus
  isActive: boolean
  onClick: () => void
  size?: "md" | "sm"
}

// Single clickable sidebar row. Two sizes: `md` for top-level
// categories, `sm` for the indented sub-category entries so the
// hierarchy reads visually without the eye having to follow the
// indent alone.
function SidebarButton(props: Readonly<SidebarButtonProps>) {
  const { label, count, status, isActive, onClick, size = "md" } = props
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2 text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        size === "md"
          ? "py-1.5 text-[13px] font-medium"
          : "py-1 text-[12px] font-normal",
        isActive
          ? "bg-foreground/10 text-foreground"
          : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
      )}
    >
      <StatusDot status={status} />
      <span className="flex-1 truncate">{label}</span>
      <span
        className={cn(
          "rounded-full px-1.5 text-[10px] tabular-nums",
          isActive
            ? "bg-foreground/15 text-foreground/80"
            : "bg-muted/40 text-muted-foreground/70",
        )}
      >
        {count}
      </span>
    </button>
  )
}

export default StatusCategorySidebar
